// Content for /for/schools — schools, colleges, universities, groups of schools and councils.
//
// TRACE: every `capability` below names the spec in openspec/specs/ that backs the claim. This
// is the audit trail for the "no claim without a shipped capability" rule (tasks 4.6). If a
// capability is ever removed, its bullet comes out of the copy with it.
//
// Written for the people who read it (2026-10-04 review): features are grouped by who uses
// them, terms like baseload and SECR are said in plain words, and the vocabulary works for a
// Scottish independent school as well as an English state one: pupils, bursars, governors,
// groups of schools and councils, not academy-trust shorthand.

export default {
    key: 'schools',
    eyebrow: 'For schools, colleges, groups of schools and councils',
    title: 'Make energy something your whole school acts on',
    subtitle:
        'Your electricity and gas readings, every half hour, turned into lessons, targets and house competitions. For a group of schools, one view across every site.',
    heroChips: [
        'Classroom activities and a weekly quiz',
        'One view across a group of schools',
        'Carbon reports for governors'
    ],

    // Whose problem each feature solves. The page renders one section per audience, in this
    // order; every feature names one of these keys.
    audiences: [
        {
            key: 'bursar',
            title: 'For the bursar and estates team',
            lede: 'Spend less, and stop paying for what nobody used.'
        },
        {
            key: 'classroom',
            title: 'For teachers, pupils and the eco-committee',
            lede: 'Energy pupils can see, measure and compete on.'
        },
        {
            key: 'leadership',
            title: 'For heads and governors',
            lede: 'Know where the school stands, and show it.'
        }
    ],

    features: [
        {
            audience: 'bursar',
            title: 'Find the holidays you are paying for',
            detail: 'Every holiday and closure costed in £ and CO₂, by building or by meter, so the cost of heating and lighting an empty school stops being invisible.',
            capability: 'holiday-analytics / holiday-analysis-ui'
        },
        {
            audience: 'bursar',
            title: 'Alerts before the bill arrives',
            detail: 'A warning when the energy a building uses overnight, with nobody in it, starts creeping up, or when spending is heading over budget. Plus a weekly or monthly summary email to the people who can act.',
            capability:
                'baseload-creep-alert / budget-breach-alert / report-subscriptions'
        },
        {
            audience: 'bursar',
            title: 'Never miss a contract renewal window',
            detail: 'Record each contract’s end date and notice period, and get a reminder before the deadline, so the school is not rolled onto expensive out-of-contract rates.',
            capability: 'supply-contract-registry / contract-renewal-alerts'
        },
        {
            audience: 'bursar',
            title: 'Check every line of the bill before you pay it',
            detail: 'Each charge on a supplier’s bill checked against what the meters recorded and the rate in your contract, so a billing error is queried before it is paid. An alert flags any bill that comes in over your threshold.',
            capability: 'bill-statement-model / bill-discrepancy-alert'
        },
        {
            audience: 'classroom',
            title: 'A weekly energy quiz about your own buildings',
            detail: 'The same questions for the whole school each week, one go each, on a house leaderboard: which building used most, which day was busiest, what a week of electricity cost. Pitched by age from 7 to 16. Teachers get a class mode for the board, a printable worksheet, and a view of which questions the class got wrong. Pupils appear by first name and initial, never by email.',
            capability: 'energy-quiz'
        },
        {
            audience: 'classroom',
            title: 'Ready-made classroom activities',
            detail: 'A library of energy activities pupils can carry out and record, and your own staff can write more.',
            capability: 'education content library (dea3baf)'
        },
        {
            audience: 'leadership',
            title: 'Set a reduction target and watch it',
            detail: 'A target for each fuel, with progress tracked against a starting point, so “use less energy” becomes a number everyone can see.',
            capability: 'energy-reduction-targets'
        },
        {
            audience: 'leadership',
            title: 'Compare against schools like yours',
            detail: 'Opt in to see how the school compares with similar schools, anonymously, alongside published figures for a typical school.',
            capability: 'anonymised-peer-benchmarking / static-benchmarks'
        },
        {
            audience: 'leadership',
            title: 'Carbon reports for governors and funders',
            detail: 'An annual carbon report in the shape the UK’s carbon reporting rules (SECR) ask for, using the government’s published conversion factors, as PDF or CSV.',
            capability:
                'carbon-report-generation / carbon-factor-tables / carbon-report-export'
        }
    ],

    asides: [
        {
            title: 'Groups of schools, trusts and councils',
            body: 'Group your schools and get a read-only view across all of them: totals, school-by-school comparison, and league tables adjusted for floor area and pupil numbers, so a large senior school and a small junior school can be compared fairly. Each school keeps its own account and its own data; the group view sits on top.',
            capability:
                'portfolio-dashboard / portfolio-rankings-ui / org groups (e9c0ad4)'
        },
        {
            title: 'Universities and colleges',
            body: 'The same platform, with the words changed to match: halls rather than houses, and a campus handled as a set of buildings with their own meters, tariffs and opening hours. Usage is adjusted for the weather, so a cold term can be compared fairly with a mild one.',
            capability:
                'sector-terminology / org-flavour / degree-day-computation'
        }
    ],

    // A real school, named with Ross's agreement (2026-10-04). Shown on the platform's own host
    // only: Fettes agreed to be named by us, not by a partner reselling under its own brand.
    reference: {
        quote: 'Fettes College in Edinburgh runs its energy monitoring on {product}.',
        platformHostOnly: true
    },

    proofPoints: [
        'Engagement pupils respond to: a weekly quiz on their own buildings, house competitions and everyday comparisons, not just a chart of kilowatt-hours',
        'Finds the energy used overnight, at weekends and over the holidays, without anyone having to go looking',
        'Reports ready for governors and funders, without rebuilding a spreadsheet each year'
    ],

    ctaTitle: 'See it with your own meter data',
    ctaBody:
        'We connect to your existing electricity and gas suppliers, so a demo can show your own school’s readings rather than a sample school.'
}
